import { useEffect } from 'react';
import type { ReactNode } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { PublicBranding } from '@cloudsign/shared';
import type { AuthStateResponse } from '@cloudsign/shared';
import { api } from '../../lib/api';
import { STAGE_PATH, safeNext, setAuthState } from '../../lib/authState';
import { applyBranding } from '../../lib/branding';
import { t } from '../../lib/i18n';
import { CAPABILITIES_KEY } from '../../lib/session';
import { LogoMark } from '../../components/Logo';

/** Moves on after any login step according to the stage the server reports. */
export function useProceed() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [params] = useSearchParams();
  return (state: AuthStateResponse) => {
    if (state.stage === 'complete') {
      qc.removeQueries({ queryKey: CAPABILITIES_KEY });
      void navigate(safeNext(params.get('next')), { replace: true });
    } else {
      const next = params.get('next');
      void navigate(`${STAGE_PATH[state.stage]}${next ? `?next=${encodeURIComponent(next)}` : ''}`, { replace: true });
    }
    setAuthState(state);
  };
}

export function AuthLayout({ title, children }: { title: ReactNode; children: ReactNode }) {
  const [params] = useSearchParams();
  const tenant = params.get('tenant') ?? undefined;
  const branding = useQuery({
    queryKey: ['publicBranding', tenant ?? null],
    queryFn: () => api.get('/public/branding', { query: { tenant }, schema: PublicBranding }),
    retry: false,
    staleTime: 5 * 60_000,
  });
  useEffect(() => {
    applyBranding(branding.data ?? null);
  }, [branding.data]);
  const name = branding.data?.displayName || t('app.name');
  return (
    <div className="flex min-h-screen">
      <div className="flex flex-1 items-center justify-center bg-white p-4 sm:p-8">
        <main className="w-full max-w-md" id="main">
          <div className="mb-8 flex items-center gap-3">
            {branding.data?.logoUrl ? <img src={branding.data.logoUrl} alt="" className="h-10 max-w-[10rem] object-contain" /> : <LogoMark />}
            <span className="truncate text-xl font-bold tracking-tight text-slate-900">{name}</span>
          </div>
          <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm sm:p-8">
            <h1 className="mb-5 text-xl font-bold tracking-tight text-slate-900">{title}</h1>
            {children}
          </div>
        </main>
      </div>
      <aside
        aria-hidden="true"
        className="relative hidden flex-1 overflow-hidden bg-gradient-to-br from-brand to-brand-hover text-on-brand lg:flex lg:flex-col lg:justify-center lg:p-16"
      >
        <div className="absolute -right-24 -top-24 h-80 w-80 rounded-full bg-white/10" />
        <div className="absolute -bottom-32 -left-16 h-96 w-96 rounded-full bg-white/10" />
        <div className="relative max-w-md">
          <svg viewBox="0 0 32 32" className="mb-8 h-14 w-14" focusable="false">
            <rect width="32" height="32" rx="9" fill="currentColor" fillOpacity="0.2" />
            <path
              d="M10.5 23a4.75 4.75 0 0 1-.9-9.41A6.25 6.25 0 0 1 21.7 12.3 5.35 5.35 0 0 1 22 23H10.5z"
              fill="currentColor"
            />
            <path d="M12.9 18.2l2.5 2.5 4.7-5" fill="none" stroke="var(--brand-primary)" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <p className="text-3xl font-bold leading-tight tracking-tight">{t('auth.promoTitle')}</p>
          <p className="mt-4 text-base opacity-90">{t('auth.promoText')}</p>
        </div>
      </aside>
    </div>
  );
}
